import type { ApiError } from '@mailflow/shared';

/**
 * HTTP-Client.
 *
 * credentials: 'include' ist zwingend — die Sitzung laeuft ueber ein
 * httpOnly-Cookie, nicht ueber einen Token im localStorage. Ein Token im
 * localStorage waere ueber jedes XSS auslesbar; ein httpOnly-Cookie nicht.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

const PROJECT_STORAGE_KEY = 'mailflow_active_project_id';

export function getActiveProjectId(): string | null {
  try {
    return localStorage.getItem(PROJECT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveProjectId(id: string): void {
  try {
    localStorage.setItem(PROJECT_STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers: Record<string, string> = body ? { 'Content-Type': 'application/json' } : {};
  const activeProject = getActiveProjectId();
  if (activeProject) headers['X-Project-Id'] = activeProject;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // Netzwerkfehler bekommen eine eigene, verstaendliche Meldung — "Failed to
    // fetch" hilft niemandem weiter.
    throw new ApiRequestError(
      0,
      'network_error',
      'Der Server ist nicht erreichbar. Besteht eine Verbindung?',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const error = (payload as ApiError | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Unerwarteter Fehler (HTTP ${response.status}).`,
      error?.fields,
    );
  }

  return payload as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, ...(signal ? { signal } : {}) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
